import { createSelector, createStructuredSelector } from 'reselect'
import { fromJS, Map, List } from 'immutable'
import {
  getEq,
  getInOr,
  getOr,
  flattenList,
  get,
  getIn,
  pick,
  safeToJS,
  pluck,
} from 'utils/immutable'

import {
  CAN_INPUT,
  SIMPLE,
  REFERENCED,
  CONFIGURABLE_INPUT_TYPES,
  NATIVE_INPUT,
  TIMER,
  SELF_REFERENCE,
  CARRY,
  CARRY_TIMER,
  CARRY_LOGIC_SET,
  COMPLEX,
  CARRY_TO,
  CHEC_MAX_VALUE,
} from 'utils/constants'
import { path, __, isNil, prop, propOr, identity } from 'ramda'
import strings from 'utils/strings'
import findTranslations from 'utils/findTranslations'
import { getInputDescriptions, signalsSelector } from 'selectors/feature/inputs'
import { getAllFunctionGraphs } from 'selectors/feature/functionGraphs'
import { createToJsSelector } from 'selectors/utils'
import { transformSwitchInputData } from './utils'

const queriesSelector = getInOr(Map(), ['feature', 'currentFeature', 'queries'])

export const conditionsSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'conditions',
])

export const conditionSelector = createSelector(
  conditionsSelector,
  (_, props) => props,
  (conditions, { queryUri }) => getOr(Map(), queryUri, conditions)
)

const querySelector = (state, props) =>
  state.getIn(['feature', 'currentFeature', 'queries', props.uri])

export const getQuery = createToJsSelector(querySelector)
export const getCondition = createToJsSelector(conditionSelector)

// TODO: extract and use in functionGraphSelector
const getRungType = (condition, signalsMap, queries) => {
  const queryTypeEq = getEq('queryType', __, condition)

  if (queryTypeEq(SIMPLE)) {
    const floatingCircuit = condition.get('floatingCircuit')
    const conditionSubtype = condition.get('subType')

    if (!isNil(floatingCircuit)) {
      return conditionSubtype
    }

    const signal = signalsMap.get(condition.get('signal'))
    if (signalsMap && signal) {
      const signalDataType = signal.get('dataType')
      return CONFIGURABLE_INPUT_TYPES.has(signalDataType)
        ? signalDataType
        : NATIVE_INPUT
    }

    return undefined
  }

  if (queryTypeEq(CAN_INPUT)) {
    return CAN_INPUT
  }

  // reference condition

  const refQueryUri = condition.get('refQueryUri')

  const isSelfReference = refQueryUri === prop('parentQueryUri', condition)
  const referenceQuery = queries.get(refQueryUri)

  const referenceQueryType = referenceQuery && referenceQuery.get('queryType')

  // self-reference timers need the CARRY_TIMER type
  if (isSelfReference && referenceQueryType !== TIMER) {
    return SELF_REFERENCE
  }
  if (!refQueryUri) {
    //  this allowes clearing of a carry condition while preserving the type if no query uri was set yet
    return CARRY
  }

  const isCarry = condition.get('subType') === 'carry'

  /* eslint-disable no-nested-ternary */
  const type =
    referenceQueryType === TIMER
      ? isCarry
        ? CARRY_TIMER
        : TIMER
      : isCarry
      ? CARRY_LOGIC_SET
      : COMPLEX
  /* eslint-enable no-nested-ternary */
  return type
}

// TODO: make into a makeSelector
export const getCarryToType = createSelector(
  queriesSelector,
  conditionsSelector,
  getAllFunctionGraphs,
  (_, props) => props,
  (queries, conditions, allGraphs, { uri, fnId, refQueryUri }) => {
    if (!refQueryUri) return null

    if (!fnId) return CARRY_TO

    if (getIn([uri, 'parentQueryUri'], conditions) === refQueryUri)
      return SELF_REFERENCE

    const currentFnColumns = fromJS(allGraphs[fnId])

    // columns are displayed in reverso of logic
    const columnsFlat = flattenList(currentFnColumns).reverse()

    const isRefQueryInCurrentFn = columnsFlat.some(
      (col) => col.get('uri') === refQueryUri
    )

    if (!isRefQueryInCurrentFn) return CARRY_TO

    let isFirstFound = true

    for (let i = 0; i < columnsFlat.size; i += 1) {
      const colBox = columnsFlat.get(i)
      const queryUri = colBox.get('uri')
      const query = queries.get(queryUri)
      const conditionUris = query.get('conditions')

      for (let j = 0; j < conditionUris.size; j += 1) {
        const conditionUri = conditionUris.get(j)
        const conditionRefQueryUri = conditions.getIn([
          conditionUri,
          'refQueryUri',
        ])

        if (conditionRefQueryUri === refQueryUri) {
          // check to see if refQueryUri has already been drawn
          // check queryUri index in columnsFlat (i) vs refQueryUri index in columnsFlat
          const refQueryUriIndex = columnsFlat.findIndex(
            getEq('uri', refQueryUri)
          )
          if (refQueryUriIndex < i) return CARRY_TO

          if (conditionUri === uri && isFirstFound) {
            // this is the first case it is referenced
            return null
          }
          if (conditionUri === uri) {
            return CARRY_TO
          }
          isFirstFound = false
        }
      }
    }

    return null
  }
)

// compute switch positions by for switch signal
export const findSwitchInputPositions = (signal) => {
  if (
    getEq('dataType', NATIVE_INPUT)(signal) &&
    getOr(List(), 'switches', signal).size > 0
  ) {
    const switchPositonsData = transformSwitchInputData(signal)
    return switchPositonsData
  }
  return Map()
}

// inputConfig is for polarity or measure when AI or DI is selected
export const mergeConditionWithSignal = (condition, signals, inputConfig) => {
  const floatingCircuit = condition.get('floatingCircuit')
  const signalUri = condition.get('signal')
  const subType = condition.get('subType')

  if (!signalUri && !floatingCircuit) return condition

  // floating circuits don't have an associated signal
  // becuase they can only be Digital Inputs or Analog Inputs
  // we can get any signal and use those translation values
  const signal = !isNil(floatingCircuit)
    ? signals.find(getEq('dataType', subType)) // find any signal where subType equals dataType
    : signals.get(signalUri)

  // For digital (DI pin) translations without explicit definedSnaValues,
  // inject [CHEC_MAX_VALUE] so the SNA option is shown in the Value radio buttons.
  const translation = findTranslations(signal, inputConfig)
  const resolvedTranslation =
    translation.get('type') === 'digital' &&
    !translation.has('definedSnaValues')
      ? translation.set('definedSnaValues', List([CHEC_MAX_VALUE]))
      : translation

  return (
    condition
      .merge(
        pick(
          [
            'dataType',
            'defaultValue',
            'translationType',
            'connector',
            'location',
            'switchNumber',
            'switchDescription',
            'relatedOutput',
          ],
          signal
        )
      )
      .merge(
        pick(
          [
            'units',
            'minValue',
            'maxValue',
            'translations',
            'definedSnaValues',
            'definedErrorValues',
          ],
          resolvedTranslation
        )
      )
      // for switch signals dataType should remain native for API POST
      // merge and get array of uris for 3 position or one uri for 2 position switch signal
      .merge(
        pick(
          ['uriOptionsByPosition', 'description'],
          findSwitchInputPositions(signal)
        )
      )
  )
}

const getSelectedInputDescription = (
  queryType,
  numberOfBits,
  canId,
  inputUri,
  inputOptions,
  signals,
  floatingCircuit
) => {
  if (floatingCircuit) return strings.floating_circuit_mask(floatingCircuit)

  if (queryType === CAN_INPUT) {
    return (
      numberOfBits &&
      canId &&
      strings.can_input_description(numberOfBits, canId)
    )
  }
  // logic for switch inputs selected description string
  const signalSwitches = getInOr(List(), [inputUri, 'switches'], signals)
  if (!signalSwitches.isEmpty()) {
    const description = signals.getIn([inputUri, 'description'])
    const signal = signals.get(inputUri)
    const relatedInputs = signal && signal.get('relatedInputs')

    // For 3-position switches in UP position, append "Up Request" to match DOWN format
    if (relatedInputs && relatedInputs.size > 0) {
      const positionDown = signal.getIn([
        'interface',
        'switchPositionDictionary',
        'switch_down',
      ])
      const isUpPosition = positionDown !== 1

      if (isUpPosition && !description.toLowerCase().includes('up request')) {
        const baseName = description.replace(
          /\s*\((?:Latching|Momentary)\)\s*$/,
          ''
        )
        return `${baseName} Up Request`
      }
    }

    return description
  }
  return path([inputUri, 'description'], inputOptions)
}

// props: queryUri, rungName
export const mergedConditionSelector = createSelector(
  queriesSelector,
  conditionSelector,
  getInputDescriptions,
  signalsSelector,
  (queries, condition, inputOptions, signals) => {
    const hasCondition = Boolean(propOr(0, 'size', condition))

    if (!hasCondition) return Map()
    const conditionProp = (condProp) => condition.get(condProp)
    const signalUri = condition.get('signal')
    const inputConfig = getOr(null, 'inputConfig', condition)
    const queryType = conditionProp('queryType')
    const refQueryUri = conditionProp('refQueryUri')
    const numberOfBits = conditionProp('numberOfBits')
    const canId = conditionProp('canId')
    const floatingCircuit = conditionProp('floatingCircuit')
    const customDescription = conditionProp('customDescription')
    const inputUri =
      queryType === REFERENCED ? refQueryUri : signalUri || floatingCircuit
    const selectedInputDescription = getSelectedInputDescription(
      queryType,
      numberOfBits,
      canId,
      inputUri,
      inputOptions,
      signals,
      floatingCircuit
    )

    const rungType = getRungType(condition, signals, queries)

    const carryType = refQueryUri ? getCarryType(condition, queries) : null

    const conditionWithSignal = mergeConditionWithSignal(
      condition,
      signals,
      inputConfig
    )

    return conditionWithSignal.merge(
      Map({
        rungType,
        carryType,
        inputUri,
        selectedInputDescription,
        customDescription,
      })
    )
  }
)

export const makeConditionSelector = () =>
  createSelector(mergedConditionSelector, safeToJS)

const getCarryType = (condition, queries) => {
  const refQueryUri = get('refQueryUri', condition)
  const parentQueryUri = get('parentQueryUri', condition)

  if (refQueryUri === parentQueryUri) return SELF_REFERENCE

  const refQueryType = getIn([refQueryUri, 'queryType'], queries)

  return refQueryType === COMPLEX ? CARRY_LOGIC_SET : CARRY_TIMER
}

export const getSimpleNodeRungName = createSelector(querySelector, (query) => {
  if (!query) return false
  return `${SIMPLE}_${query.get('subType')}`
})

const selectSetSimpleConditions = createSelector(
  queriesSelector,
  (conditions) => {
    if (!conditions) return Map()
    return (
      conditions &&
      conditions.filter((condition) => {
        const queryType = condition.get('queryType')
        const signal = condition.get('signal')
        return queryType === SIMPLE && signal
      })
    )
  }
)

export const getSetSimpleConditions = createToJsSelector(
  selectSetSimpleConditions
)

const scalingQueryPropSelector =
  (key) =>
  (state, { uri }) =>
    state.getIn(['feature', 'currentFeature', 'scalingQueries', uri, key])

export const getScalingQueryProps = createStructuredSelector({
  valueMin: scalingQueryPropSelector('valueMin'),
  valueMax: scalingQueryPropSelector('valueMax'),
  signal: scalingQueryPropSelector('signal'),
  inputConfig: scalingQueryPropSelector('inputConfig'),
  subType: scalingQueryPropSelector('subType'),
  numberOfBits: scalingQueryPropSelector('numberOfBits'),
  startBit: scalingQueryPropSelector('startBit'),
  canId: scalingQueryPropSelector('canId'),
})

export const makeGetScalingQueryProps = () => getScalingQueryProps

export const conditionsToResetFloatingCircuits = createSelector(
  queriesSelector,
  (_, props) => props.availableFloatingCircuits,
  (queries, availableFloatingCircuits) => {
    const conditionsToReset = queries.filter((query) => {
      const floatingCircuit = query.get('floatingCircuit')

      if (isNil(floatingCircuit)) return false

      return !availableFloatingCircuits.includes(floatingCircuit)
    })

    return pluck('uri', conditionsToReset)
  }
)

const conditionEvaluationSelector = (state, props) =>
  state.getIn(['feature', 'currentFeature', 'conditions', props.uri, 'result'])

export const makeGetConditionEvaluation = () =>
  createSelector(conditionEvaluationSelector, identity)
